import React, { useMemo } from 'react';
import {
    Box,
    Button,
    Icon,
    IconNameT,
    MediaGalleryViewer,
    prettyBytes,
    Text,
    VStack,
} from '@cad-arc';
import { CombinedMediaItem } from './types';
import { useAttachmentSizeLimits } from './hooks/use-attachment-size-limits';
import 'vidstack/bundle';
import { canPreviewAttachment } from './helpers';
import { electronBridge } from '../electron-bridge/electron-bridge';

interface CustomMediaItemViewerProps {
    combinedItems: CombinedMediaItem[];
    selectedId: string | null;
}

const getMediaTypeIcon = (type: CombinedMediaItem['type']): IconNameT => {
    switch (type) {
        case 'pdf': {
            return 'PDFDocument';
        }
        case 'image': {
            return 'Image';
        }
        case 'video': {
            return 'VideoFile';
        }
        case 'audio': {
            return 'AudioFile';
        }
        default: {
            return 'Document';
        }
    }
};

const getMediaTypeDisplayName = (type: CombinedMediaItem['type']): string => {
    switch (type) {
        case 'pdf': {
            return 'PDF';
        }
        case 'image': {
            return 'Image';
        }
        case 'video': {
            return 'Video';
        }
        case 'audio': {
            return 'Audio';
        }
        default: {
            return 'File';
        }
    }
};

const MediaExceedsPreviewLimitMessage = ({
    fileSizeBytes,
    maxPreviewSizeBytes,
    mediaType,
    attachmentUrl,
}: {
    fileSizeBytes: number | undefined;
    maxPreviewSizeBytes?: number | null;
    mediaType: CombinedMediaItem['type'];
    attachmentUrl?: string;
}) => {
    const openAttachmentInBrowser = () => {
        if (!attachmentUrl) {
            return;
        }

        if (electronBridge) {
            electronBridge.openUrlInBrowser(attachmentUrl);
        } else {
            window.open(attachmentUrl, '_blank');
        }
    };

    return (
        <Box width="100%" height="100%" display="flex" alignItems="center" justifyContent="center">
            <VStack spacing="md" alignItems="center">
                <Icon icon={getMediaTypeIcon(mediaType)} size="lg" />
                <Text fontSize="lg" fontWeight="semibold">
                    {getMediaTypeDisplayName(mediaType)} Exceeds Preview Limit
                </Text>
                {!!maxPreviewSizeBytes && (
                    <>
                        {fileSizeBytes && (
                            <Text color="text.secondary">
                                File size: {prettyBytes(fileSizeBytes)}
                            </Text>
                        )}
                        <Text color="text.secondary">
                            Maximum preview size: {prettyBytes(maxPreviewSizeBytes)}
                        </Text>
                    </>
                )}
                {attachmentUrl ? (
                    <Button
                        variant="outline"
                        trailingVisual="OpenNewWindow"
                        onClick={openAttachmentInBrowser}
                        mt={4}
                    >
                        View
                    </Button>
                ) : null}
            </VStack>
        </Box>
    );
};

export function CustomMediaItemViewer({
    combinedItems,
    selectedId,
}: CustomMediaItemViewerProps): React.ReactElement {
    const sizeLimits = useAttachmentSizeLimits();
    const currentItem = combinedItems.find((item) => item.id === selectedId);

    const mediaStyles = useMemo(() => {
        const styles: Record<string, any> = {
            // add margin to the bottom of the media gallery viewer to prevent counter
            // from crashing into video seek bar / very long file name
            '.arc-media-gallery-viewer': {
                marginBottom: '40px',
            },
        };

        if (currentItem?.type === 'htmlVideo' || currentItem?.type === 'video') {
            // ensure video player takes up full space
            styles['.arc-media-player'] = {
                width: '100%',
                height: '100%',
                aspectRatio: 'auto',
            };
        }
        return styles;
    }, [currentItem]);

    if (!currentItem) {
        return (
            <Box
                position="relative"
                width="100%"
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                No Attachment Selected
            </Box>
        );
    }

    return (
        <Box width="100%" height="100%" display="flex" flexDirection="column" sx={mediaStyles}>
            <MediaGalleryViewer
                aspectRatio="auto"
                showArrows={true}
                showCounter={false}
                renderPreview={(item) => {
                    const currentItem = item as CombinedMediaItem;
                    // check if item is too large to preview
                    if (
                        !canPreviewAttachment(
                            currentItem.type,
                            currentItem.fileSizeBytes,
                            sizeLimits
                        )
                    ) {
                        return (
                            <MediaExceedsPreviewLimitMessage
                                fileSizeBytes={currentItem?.fileSizeBytes}
                                maxPreviewSizeBytes={sizeLimits.getMaxPreviewSizeForItem(
                                    currentItem
                                )}
                                mediaType={currentItem?.type}
                                attachmentUrl={currentItem?.attachmentUrl}
                            />
                        );
                    }

                    // return null to fall back to Arc's default preview
                    return null;
                }}
            />
        </Box>
    );
}
